import {useEffect, useState} from 'react';
import {useApolloClient, useMutation, useQuery} from '@apollo/client';
import {Alert, Select, Spin} from 'antd';
import {toast} from 'react-toastify';
import ExchangeSelect from 'components/exchangeSelect/ExchangeSelect';
import {actionError, unionError} from '../shared/errors';
import {ActionButton, Tip} from '../shared/ui';
import ScopeQuestions, {answersProblem} from './ScopeQuestions';
import {
  ATTACH_NEWS_SOURCE_COVERAGE,
  GET_NEWS_SOURCES,
  PROBE_NEWS_SOURCE,
  UPDATE_NEWS_SOURCE,
} from './resolvers';
import {ADD, GNEWS_EDITIONS, PAGE, TOAST} from './copy';
import {
  answersFromCoverage,
  coverageFromAnswers,
  exchangeDefaults,
  readsOtherSitesFeed,
  replaceCoverage,
} from './sourceHelpers';
import styles from '../shared/news.module.scss';

// One exchange's settings for a website: edits an existing feed (`row`), or
// adds the website to another exchange when `row` is null. The website is
// checked again in the background so the questions work on fresh articles.
const FeedEditor = ({source, row, exchanges, canUpdate, onSaved}) => {
  const client = useApolloClient();
  const [exchangeId, setExchangeId] = useState(row?.exchangeId);
  const [answers, setAnswers] = useState(() =>
    answersFromCoverage(row || {requireKeywords: true}),
  );
  const [gnewsLocale, setGnewsLocale] = useState(row?.gnewsLocale);
  const [samples, setSamples] = useState(null);

  const pinnedFeed =
    source.discovery?.mode && source.discovery.mode !== 'AUTO'
      ? source.discovery.feedUrl
      : undefined;

  useEffect(() => {
    let cancelled = false;
    client
      .query({
        query: PROBE_NEWS_SOURCE,
        variables: {
          homepageUrl: source.homepageUrl,
          gnewsLocale: row?.gnewsLocale,
          feedUrl: pinnedFeed,
        },
        fetchPolicy: 'no-cache',
      })
      .then(({data}) => {
        if (!cancelled) setSamples(data?.probeNewsSource?.sampleArticles || []);
      })
      .catch(err => {
        console.error(err);
        if (!cancelled) setSamples([]);
      });
    return () => {
      cancelled = true;
    };
  }, [client, source.homepageUrl, row?.gnewsLocale, pinnedFeed]);

  // A new exchange starts from the words and edition its websites use.
  const exchange = exchanges.find(item => item.id === exchangeId);
  const {data: exchangeSources} = useQuery(GET_NEWS_SOURCES, {
    variables: {exchangeId, limit: 100},
    skip: Boolean(row) || !exchangeId,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  useEffect(() => {
    const list = exchangeSources?.newsSources?.data;
    if (!list) return;
    const defaults = exchangeDefaults(exchange, list);
    setAnswers(prev => ({...prev, keywords: defaults.keywords}));
    setGnewsLocale(defaults.gnewsLocale);
  }, [exchangeSources, exchange]);

  const onError = actionError('save these settings');
  const [update, {loading: updating}] = useMutation(UPDATE_NEWS_SOURCE, {
    onError,
  });
  const [attach, {loading: attaching}] = useMutation(
    ATTACH_NEWS_SOURCE_COVERAGE,
    {onError},
  );

  const code = row?.exchangeCode || exchange?.code || '';
  const problem = answersProblem(
    answers,
    readsOtherSitesFeed(source.homepageUrl, source.discovery?.feedUrl),
  );

  const save = async () => {
    const fields = {...coverageFromAnswers(answers, row), gnewsLocale};
    const {data} = row
      ? await update({
          variables: {
            sourceId: source.id,
            data: {coverage: replaceCoverage(source, row.exchangeId, fields)},
          },
        })
      : await attach({
          variables: {
            sourceId: source.id,
            coverage: {
              exchangeId,
              exchangeCode: exchange?.code,
              exchangeName: exchange?.stockExchangeName,
              enabled: true,
              ...fields,
            },
          },
        });
    const result =
      data?.[row ? 'updateNewsSource' : 'attachNewsSourceCoverage'];
    if (!result) return;
    if (result.__typename === 'Error') {
      return unionError('save these settings', result);
    }
    toast.success(
      row ? TOAST.saved(source.name) : TOAST.added(source.name, code),
    );
    onSaved();
  };

  const taken = (source.coverage || []).map(item => item.exchangeId);

  return (
    <div className={styles.modalRoot}>
      <h2>
        {row
          ? PAGE.editTitle(source.name, code)
          : PAGE.feedAnotherTitle(source.name)}
      </h2>

      {row ? null : (
        <div className={styles.block}>
          <h4>
            <Tip label={PAGE.whichExchange} tip={PAGE.whichExchangeTip} />
          </h4>
          <ExchangeSelect
            className={styles.fullWidth}
            value={exchangeId}
            onChange={setExchangeId}
            exclude={taken}
          />
        </div>
      )}

      {samples === null ? (
        <p className={styles.reason}>
          <Spin size="small" /> {PAGE.loadingSamples}
        </p>
      ) : null}
      <ScopeQuestions
        value={answers}
        onChange={setAnswers}
        exchange={code || '…'}
        host={source.host}
        samples={samples || []}
      />

      <div className={styles.block}>
        <h4>
          <Tip label={ADD.gnews} tip={ADD.gnewsTip} />
        </h4>
        <Select
          className={styles.fullWidth}
          value={gnewsLocale}
          onChange={setGnewsLocale}
          options={GNEWS_EDITIONS}
        />
      </div>

      {problem ? (
        <Alert
          type="warning"
          showIcon
          className={styles.block}
          message={problem}
        />
      ) : null}

      <div className={styles.modalActions}>
        <ActionButton
          type="primary"
          allowed={canUpdate}
          tip={row ? PAGE.editTip : PAGE.feedAnotherTip}
          disabled={Boolean(problem) || (!row && !exchangeId)}
          loading={updating || attaching}
          onClick={save}>
          {row ? 'Save' : ADD.saveToExchange(code || '…')}
        </ActionButton>
      </div>
    </div>
  );
};

export default FeedEditor;
